import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MagnifyingGlass, CaretLeft, CaretRight, PaperPlaneRight } from "@phosphor-icons/react";
import { FilePicker, AttachmentList } from "@/pages/SupportPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = { open: "Aperto", in_progress: "In lavorazione", resolved: "Risolto", closed: "Chiuso" };
const STATUS_COLOR = {
  open: "bg-orange-100 text-orange-700",
  in_progress: "bg-blue-100 text-blue-700",
  resolved: "bg-green-100 text-green-700",
  closed: "bg-gray-100 text-gray-700",
};
const PRIORITY_COLOR = { low: "bg-gray-100 text-gray-700", normal: "bg-blue-100 text-blue-700", high: "bg-red-100 text-red-700" };
const PRIORITY_LABEL = { low: "Bassa", normal: "Normale", high: "Alta" };

export default function AdminTicketsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 25;
  const [statusF, setStatusF] = useState("all");
  const [priorityF, setPriorityF] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (statusF !== "all") params.set("status", statusF);
      if (priorityF !== "all") params.set("priority", priorityF);
      if (search) params.set("search", search);
      const { data } = await axios.get(`${API}/admin/tickets?${params}`, { withCredentials: true });
      setItems(data.items); setTotal(data.total);
    } catch { toast.error("Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, statusF, priorityF]);

  const openTicket = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/tickets/${id}`, { withCredentials: true });
      setThread(data); setSelected(id); setReply(""); setReplyFiles([]);
    } catch { toast.error("Errore"); }
  };

  const handleReply = async () => {
    if (!reply.trim() && replyFiles.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("body", reply);
      replyFiles.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/admin/tickets/${selected}/messages`, fd, { withCredentials: true });
      toast.success("Risposta inviata"); setReply(""); setReplyFiles([]);
      await openTicket(selected); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const handleUpdate = async (changes) => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/tickets/${selected}`, changes, { withCredentials: true });
      toast.success("Aggiornato");
      await openTicket(selected); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4" data-testid="admin-tickets-page">
      <div>
        <h1 className="text-3xl font-bold text-[#0D1F3C]">Console Admin · Ticket</h1>
        <p className="text-sm text-[#0D1F3C]/60 mt-1">{total} ticket · pagina {page}/{totalPages}</p>
      </div>
      <div className="bg-white border border-[#0D1F3C]/8 rounded-xl p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1F3C]/40" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
            placeholder="Cerca per subject..." className="pl-9" />
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-[160px]" data-testid="admin-tickets-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={priorityF} onValueChange={setPriorityF}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Priorità" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte</SelectItem>
            {Object.entries(PRIORITY_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
          </SelectContent>
        </Select>
        <Button onClick={() => { setPage(1); load(); }} variant="outline">Filtra</Button>
      </div>
      <div className="bg-white border border-[#0D1F3C]/8 rounded-xl overflow-hidden">
        <div className="max-h-[calc(100vh-340px)] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0D1F3C] text-white text-left sticky top-0">
              <tr>
                <th className="px-4 py-3">Quando</th>
                <th className="px-4 py-3">Utente</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Priorità</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Ultima attività</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="text-center py-8 text-[#0D1F3C]/40">Caricamento…</td></tr> :
               items.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-[#0D1F3C]/40">Nessun ticket</td></tr> :
               items.map((t) => (
                <tr key={t.id} className="border-t border-[#0D1F3C]/5 hover:bg-[#F4F7FB] cursor-pointer"
                    onClick={() => openTicket(t.id)} data-testid={`admin-ticket-row-${t.id}`}>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">{new Date(t.created_at).toLocaleString("it-IT")}</td>
                  <td className="px-4 py-3 text-xs">{t.user_email || "—"}{t.user_company && <div className="text-[10px] opacity-60">{t.user_company}</div>}</td>
                  <td className="px-4 py-3 font-medium text-[#0D1F3C]">{t.subject}</td>
                  <td className="px-4 py-3"><Badge className={PRIORITY_COLOR[t.priority]}>{PRIORITY_LABEL[t.priority] || t.priority}</Badge></td>
                  <td className="px-4 py-3"><Badge className={STATUS_COLOR[t.status]}>{STATUS_LABEL[t.status] || t.status}</Badge></td>
                  <td className="px-4 py-3 text-xs text-[#0D1F3C]/60">{new Date(t.updated_at).toLocaleString("it-IT")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 bg-[#F4F7FB] border-t border-[#0D1F3C]/5">
          <span className="text-xs text-[#0D1F3C]/60">{total} ticket</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}><CaretLeft size={14} /></Button>
            <span className="text-sm font-medium">{page} / {totalPages}</span>
            <Button size="sm" variant="outline" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}><CaretRight size={14} /></Button>
          </div>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && (setSelected(null), setThread(null))}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-ticket-thread">
          <DialogHeader>
            <DialogTitle>{thread?.subject || "Ticket"}</DialogTitle>
            <p className="text-xs text-[#0D1F3C]/60">Da: {thread?.user?.email} ({thread?.user_company || "—"})</p>
          </DialogHeader>
          {thread && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Select value={thread.status} onValueChange={(v) => handleUpdate({ status: v })}>
                  <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="admin-ticket-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                </Select>
                <Select value={thread.priority} onValueChange={(v) => handleUpdate({ priority: v })}>
                  <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="admin-ticket-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PRIORITY_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="bg-[#F4F7FB] rounded-lg p-4">
                <div className="text-xs text-[#0D1F3C]/60 mb-1">{thread.user_email} · {new Date(thread.created_at).toLocaleString("it-IT")}</div>
                <div className="text-sm whitespace-pre-wrap">{thread.body}</div>
                <AttachmentList ticketId={thread.id} attachments={thread.attachments} />
              </div>
              {(thread.messages || []).map((m) => (
                <div key={m.id} className={`rounded-lg p-4 ${m.from_admin_id ? "bg-blue-50 ml-8" : "bg-[#F4F7FB] mr-8"}`}>
                  <div className="text-xs text-[#0D1F3C]/60 mb-1">
                    {m.from_admin_id ? `Admin · ${m.from_admin_email}` : `Utente · ${m.from_user_email}`}
                    {" · "}{new Date(m.created_at).toLocaleString("it-IT")}
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                  <AttachmentList ticketId={thread.id} attachments={m.attachments} />
                </div>
              ))}
              <div className="border-t border-[#0D1F3C]/8 pt-3 space-y-2">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Scrivi una risposta..." rows={3} data-testid="admin-ticket-reply" />
                <FilePicker files={replyFiles} setFiles={setReplyFiles} testId="admin-reply-files" />
                <div className="flex justify-end">
                  <Button onClick={handleReply} disabled={busy || (!reply.trim() && replyFiles.length === 0)} data-testid="admin-ticket-send-reply">
                    <PaperPlaneRight size={14} className="mr-2" />Invia risposta
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
